import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, XIcon, ArrowRightIcon, LayersIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useBrand } from '../../hooks/useBrand';
import { genres, subgenres, tropes, themes } from '../../data/bookTags';
const QUICK_MOODS = ['Dark Romance', 'Hockey Romance', 'Why Choose', 'Enemies to Lovers', 'Small Town Romance', 'Morally Grey Hero', 'Slow Burn', 'Paranormal Romance'];
// Build a dictionary of all known tags for parsing
const KNOWN_TAGS = [...genres.map(g => ({
  id: g.id,
  name: g.name.toLowerCase()
})), ...subgenres.map(s => ({
  id: s.id,
  name: s.name.toLowerCase()
})), ...tropes.map(t => ({
  id: t.id,
  name: t.name.toLowerCase()
})), ...themes.map(t => ({
  id: t.id,
  name: t.name.toLowerCase()
}))];
interface StoryStackHeroProps {
  onMoodSelect?: (tags: string[]) => void;
}
const StoryStackHero: React.FC<StoryStackHeroProps> = ({
  onMoodSelect
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    theme,
    content
  } = useBrand();
  const navigate = useNavigate();
  const [moodInput, setMoodInput] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  // Notify parent when tags change
  useEffect(() => {
    if (onMoodSelect) {
      onMoodSelect(selectedTags);
    }
  }, [selectedTags, onMoodSelect]);
  const accentBg = theme.hero.bg;
  const accentBgHover = theme.hero.bgHover;
  const accentBorder = theme.hero.border;
  const accentText = theme.hero.text;
  const accentFocusBorder = theme.hero.focusBorder;
  const accentHoverBorder = theme.hero.hoverBorder;
  const accentHoverText = theme.hero.hoverText;
  const accentBtnText = theme.hero.onBg;
  const handleAddTag = (tag: string) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
    }
  };
  const handleRemoveTag = (tag: string) => {
    setSelectedTags(selectedTags.filter(t => t !== tag));
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!moodInput.trim()) return;
    const inputLower = moodInput.toLowerCase();
    let foundTags: string[] = [];
    KNOWN_TAGS.forEach(tag => {
      if (inputLower.includes(tag.name)) {
        const quickMood = QUICK_MOODS.find(m => m.toLowerCase() === tag.name);
        const formattedTag = quickMood || tag.name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        foundTags.push(formattedTag);
      }
    });
    if (foundTags.length > 0) {
      const newTags = [...selectedTags];
      foundTags.forEach(tag => {
        if (!newTags.includes(tag)) newTags.push(tag);
      });
      setSelectedTags(newTags);
    } else {
      handleAddTag(moodInput.trim());
    }
    setMoodInput('');
  };
  const handleExplore = () => {
    navigate('/story-stack');
  };
  return <div className={`relative overflow-hidden rounded-2xl shadow-lg border ${dk.card}`} data-id="element-1865" data-mp="lvyjvgu28dh0">
      {/* Background decorative element */}
      <div className={`absolute top-0 right-0 -mt-10 -mr-10 ${accentText} opacity-[0.06] pointer-events-none`} data-id="element-1866" data-mp="byafs0akdtm6">
        <LayersIcon className="w-40 h-40 md:w-48 md:h-48" data-id="element-1867" data-mp="6yfohvmvn20l" />
      </div>

      <div className="relative p-4 sm:p-6 md:p-8" data-id="element-1868" data-mp="2ydum8ndvcvv">
        {/* Two-column layout on desktop, stacked on mobile */}
        <div className="flex flex-col md:flex-row md:items-start md:gap-8" data-id="element-1869" data-mp="3uc9rkdw75cz4">
          {/* Left: Headline + Search + Stack preview */}
          <div className="flex-1 min-w-0" data-id="element-1870" data-mp="3pdq7yk52sd9x">
            <h2 className={`text-lg sm:text-xl md:text-2xl font-bold mb-1.5 sm:mb-2 ${dk.textHeading}`} data-id="element-1871" data-mp="3kelvnkmutzfd">
              {content.storyStack.heroTitle}
            </h2>
            <p className={`text-xs sm:text-sm mb-3 sm:mb-4 ${dk.textSecondary}`} data-id="element-1872" data-mp="3bdxkcxouid66">
              {content.storyStack.heroSubtitle}
            </p>

            {/* Search input */}
            <form onSubmit={handleSearch} className="mb-3 sm:mb-4" data-id="element-1873" data-mp="8yjxtqvb7e24">
              <div className="flex gap-2 sm:gap-0 sm:relative" data-id="element-1874" data-mp="8zv1zrso2ntr">
                <div className="relative flex-1" data-id="element-1875" data-mp="3rb8cuolwypxg">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${dk.textMuted}`} data-id="element-1876" data-mp="3vdldwzpptznw" />
                  <input type="text" value={moodInput} onChange={e => setMoodInput(e.target.value)} placeholder={content.storyStack.placeholder} className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-20 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base border-2 focus:outline-none ${accentFocusBorder} transition-colors ${dk.input} shadow-sm`} data-id="element-1877" data-mp="3hcirglut6ncg" />
                  <button type="submit" className={`hidden sm:block absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-1.5 ${accentBg} ${accentBgHover} ${accentBtnText} rounded-md font-semibold text-sm transition-colors`} data-id="element-1878" data-mp="1anw2yxs4uy0h">
                    Add
                  </button>
                </div>
                <button type="submit" className={`sm:hidden flex-shrink-0 px-4 py-2.5 ${accentBg} ${accentBgHover} ${accentBtnText} rounded-lg font-semibold text-sm transition-colors`} data-id="element-1879" data-mp="34xazxmrki2vf">
                  Add
                </button>
              </div>
            </form>

            {/* Selected stack preview — compact inline */}
            {selectedTags.length > 0 && <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3 sm:mb-0" data-id="element-1880" data-mp="3iv90npcyu0sg">
                <span className={`text-xs font-semibold uppercase tracking-wider mr-1 ${dk.textMuted}`} data-id="element-1881" data-mp="1tg2wp4snqssu">
                  Stack:
                </span>
                {selectedTags.map(tag => <span key={tag} className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 sm:py-1 ${rt.badgeRadius} ${accentBg} ${accentBtnText} text-xs font-medium`} data-id="element-1882" data-mp="3ok0tq8u044pc">
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)} className="ml-1 opacity-70 hover:opacity-100" data-id="element-1883" data-mp="2edqpibn0l1q">
                      <XIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3" data-id="element-1884" data-mp="bcfprsbt93md" />
                    </button>
                  </span>)}
                <button onClick={handleExplore} className={`inline-flex items-center text-xs font-semibold ml-1 ${accentText} hover:underline`} data-id="element-1885" data-mp="l2r66ecol5a5">
                  Explore →
                </button>
              </div>}
          </div>

          {/* Right: Quick mood pills */}
          <div className="md:w-[280px] lg:w-[320px] flex-shrink-0" data-id="element-1886" data-mp="z31sr56yjxpi">
            <p className={`hidden md:block text-xs font-semibold uppercase tracking-wider mb-2 ${dk.textMuted}`} data-id="element-1887" data-mp="2jkuc5r6l0rej">
              Popular moods
            </p>
            <div className="flex flex-wrap gap-1.5 sm:gap-2" data-id="element-1888" data-mp="2ajcui4dj0hnq">
              {QUICK_MOODS.map(mood => <button key={mood} onClick={() => handleAddTag(mood)} className={`px-2.5 py-1 sm:px-3 sm:py-1.5 ${rt.badgeRadius} text-xs font-medium border transition-all ${selectedTags.includes(mood) ? `${accentBg} ${accentBtnText} ${accentBorder}` : `${dk.surfaceMuted} ${dk.textSecondary} ${accentHoverBorder} ${accentHoverText}`}`} data-id="element-1889" data-mp="25l2zpuvprj2n">
                  {mood}
                </button>)}
            </div>
            {/* Explore button on desktop when tags selected */}
            {selectedTags.length > 0 && <button onClick={handleExplore} className={`hidden md:flex items-center justify-center w-full mt-3 py-2 rounded-lg ${accentBg} ${accentBgHover} ${accentBtnText} font-semibold text-sm transition-colors shadow-sm`} data-id="element-1890" data-mp="3h7jgw8so5i43">
                Explore your Story Stack
                <ArrowRightIcon className="ml-1.5 h-4 w-4" data-id="element-1891" data-mp="2fndh6gwwhkb9" />
              </button>}
          </div>
        </div>
      </div>
    </div>;
};
export default StoryStackHero;